import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import despachoCombustibleService from '../../services/despachoCombustibleService';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';
import LocationPicker from '../../components/logistica/LocationPicker.jsx';
import {
  Page, Header, Title, Controls, SearchInput, BackBtn, AddBtn,
  TableWrap, Table, Th, Td, ActionsCell, ActionBtn,
  Overlay, ModalBox, ModalHead, ModalTitle, CloseBtn, ModalBody, ModalFoot,
  FormGrp, Lbl, Inp, Row2, ErrMsg, BtnPrimary, BtnSecondary, BtnDanger,
} from '../../components/configuracion/logisticaLayoutStyles';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';
import ImportarModal from '../../components/importacion/ImportarModal';

const SectionLabel = styled.div`
  font-size: 0.75rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.txtSecondary}; margin: 1.25rem 0 0.5rem;
  padding-bottom: 0.25rem; border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  display: flex; align-items: center; justify-content: space-between;
`;

const CisternaRow = styled.div`
  display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;

const CisternaNombre = styled.div`
  flex: 1; min-width: 0;
  color: ${({ theme }) => theme.colors.txtPrincipal}; font-size: 0.9rem;
  small { display: block; color: ${({ theme }) => theme.colors.txtSecondary}; font-size: 0.76rem; }
`;

const EmptyHint = styled.div`
  color: ${({ theme }) => theme.colors.txtSecondary}; font-size: 0.84rem;
  padding: 0.5rem 0; font-style: italic;
`;

const EMPTY_LUGAR = { nombre: '', direccion: '', telefono: '', contacto: '', correo: '', latitud: '', longitud: '', observaciones: '', activo: true };
const EMPTY_CISTERNA = { nombre: '', identificacion: '', capacidad_litros: '', activo: true };

export default function ConfiguracionLugaresDespacho() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const backTo = from === 'config-combustible' ? '/configuracion?seccion=combustible' : '/combustible';
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY_LUGAR);
  const [mapLocation, setMapLocation] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportCisternasModal, setShowImportCisternasModal] = useState(false);

  // Cisternas del lugar en edición.
  const [cisternas, setCisternas] = useState([]);
  const [cisternaForm, setCisternaForm] = useState(EMPTY_CISTERNA);
  const [cisternaEdit, setCisternaEdit] = useState(null);
  const [cisternaErr, setCisternaErr] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await despachoCombustibleService.getLugares();
      setItems(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return items;
    const q = busqueda.toLowerCase();
    return items.filter(i =>
      i.nombre?.toLowerCase().includes(q) || i.direccion?.toLowerCase().includes(q)
    );
  }, [items, busqueda]);

  const abrir = (item = null) => {
    setEditando(item);
    const lat = item?.latitud != null ? String(item.latitud) : '';
    const lng = item?.longitud != null ? String(item.longitud) : '';
    setForm(item ? {
      nombre: item.nombre || '', direccion: item.direccion || '',
      telefono: item.telefono || '', contacto: item.contacto || '', correo: item.correo || '',
      latitud: lat, longitud: lng,
      observaciones: item.observaciones || '', activo: item.activo ?? true,
    } : EMPTY_LUGAR);
    setMapLocation(lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng), direccion: item?.direccion || '' } : null);
    setCisternas(item?.cisternas || []);
    setCisternaForm(EMPTY_CISTERNA);
    setCisternaEdit(null);
    setCisternaErr({});
    setErrors({});
    setModal(true);
  };

  const handleMapLocation = (data) => {
    setMapLocation(data);
    if (!data) { setForm(f => ({ ...f, latitud: '', longitud: '' })); return; }
    const lat = data.lat != null && !isNaN(data.lat) ? String(data.lat) : '';
    const lng = data.lng != null && !isNaN(data.lng) ? String(data.lng) : '';
    setForm(f => ({
      ...f, latitud: lat, longitud: lng,
      direccion: data.direccion || data.address || f.direccion,
    }));
  };

  const cerrar = () => { setModal(false); setEditando(null); };
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };

  const guardar = async () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const toNum = (v) => { const n = parseFloat(v); return isNaN(n) ? null : parseFloat(n.toFixed(8)); };
      const payload = {
        nombre: form.nombre.trim(), direccion: form.direccion,
        telefono: form.telefono, contacto: form.contacto, correo: form.correo,
        latitud: toNum(form.latitud), longitud: toNum(form.longitud),
        observaciones: form.observaciones, activo: form.activo,
      };
      if (editando) await despachoCombustibleService.actualizarLugar(editando.id, payload);
      else await despachoCombustibleService.crearLugar(payload);
      cerrar(); cargar();
    } catch (err) {
      const detail = err.response?.data;
      setErrors(detail && typeof detail === 'object' ? detail : { nombre: 'Error al guardar.' });
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    try { await despachoCombustibleService.eliminarLugar(id); setConfirmId(null); cargar(); }
    catch (e) { console.error(e); }
  };

  // ─── Cisternas ───────────────────────────────────────────────────────────────
  const setCist = (k, v) => { setCisternaForm(f => ({ ...f, [k]: v })); setCisternaErr({}); };

  const guardarCisterna = async () => {
    if (!editando) return;
    if (!cisternaForm.nombre.trim()) { setCisternaErr({ nombre: 'Nombre requerido' }); return; }
    const payload = {
      lugar: editando.id, nombre: cisternaForm.nombre.trim(),
      identificacion: cisternaForm.identificacion,
      capacidad_litros: cisternaForm.capacidad_litros !== '' ? parseFloat(cisternaForm.capacidad_litros) : null,
      activo: cisternaForm.activo,
    };
    try {
      if (cisternaEdit) await despachoCombustibleService.actualizarCisterna(cisternaEdit, payload);
      else await despachoCombustibleService.crearCisterna(payload);
      const nuevas = await despachoCombustibleService.getCisternas({ lugar: editando.id });
      setCisternas(nuevas); setCisternaForm(EMPTY_CISTERNA); setCisternaEdit(null);
    } catch (err) {
      const detail = err.response?.data;
      setCisternaErr(detail && typeof detail === 'object' ? detail : { nombre: 'Error al guardar.' });
    }
  };

  const editarCisterna = (c) => {
    setCisternaEdit(c.id);
    setCisternaForm({
      nombre: c.nombre || '', identificacion: c.identificacion || '',
      capacidad_litros: c.capacidad_litros != null ? String(c.capacidad_litros) : '',
      activo: c.activo ?? true,
    });
  };

  const eliminarCisterna = async (id) => {
    try {
      await despachoCombustibleService.eliminarCisterna(id);
      setCisternas(cs => cs.filter(c => c.id !== id));
      if (cisternaEdit === id) { setCisternaEdit(null); setCisternaForm(EMPTY_CISTERNA); }
    } catch (e) { console.error(e); }
  };

  return (
    <Page>
      <Header>
        <Title><i className="bi bi-fuel-pump" /> Lugares de Despacho</Title>
        <HelpToggleBtn
          type="button"
          $active={helpOpen}
          onClick={() => setHelpOpen(v => !v)}
        >
          <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
          Ayuda
        </HelpToggleBtn>
      </Header>

      {helpOpen && (
        <div style={{ marginBottom: '1rem' }}>
          <HelpPanel
            tips={[
              'Son los puntos desde donde se carga combustible: surtidores fijos, tanques de obra o cisternas móviles.',
              'Cada despacho descuenta del stock del lugar donde se cargó.',
              'Separar los lugares permite detectar diferencias de stock y controlar cada punto por separado.',
            ]}
            nextSteps={[
              'Registrar los despachos de combustible indicando el lugar correspondiente.',
              'Controlar periódicamente el stock de cada lugar contra las cargas registradas.',
            ]}
            inlineOpen
          />
        </div>
      )}
      <Controls>
        <SearchInput $width="280px" placeholder="Buscar lugar..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <BackBtn onClick={() => navigate(backTo)}><i className="fas fa-arrow-left" /> Volver</BackBtn>
          <BackBtn onClick={() => setShowImportCisternasModal(true)}>
            <i className="fas fa-file-import" /> Importar cisternas
          </BackBtn>
          <BackBtn onClick={() => setShowImportModal(true)}>
            <i className="fas fa-file-import" /> Importar
          </BackBtn>
          <AddBtn onClick={() => abrir()}><i className="fas fa-plus" /> Agregar</AddBtn>
        </div>
      </Controls>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Nombre</Th>
              <Th>Dirección</Th>
              <Th>Contacto</Th>
              <Th>Teléfono</Th>
              <Th>Cisternas</Th>
              <Th>Estado</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><Td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Cargando...</Td></tr>}
            {!loading && filtrados.length === 0 && <tr><Td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Sin registros</Td></tr>}
            {filtrados.map(item => (
              <tr key={item.id}>
                <Td>{item.nombre}</Td>
                <Td>{item.direccion || '-'}</Td>
                <Td>{item.contacto || '-'}</Td>
                <Td>{item.telefono || '-'}</Td>
                <Td>{item.cisternas?.length || 0}</Td>
                <Td>{item.activo ? 'Activo' : 'Inactivo'}</Td>
                <ActionsCell>
                  <ActionBtn onClick={() => abrir(item)}>Editar</ActionBtn>
                  <ActionBtn $danger onClick={() => setConfirmId(item.id)}>Eliminar</ActionBtn>
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {modal && (
        <Overlay onMouseDown={onBackdropMouseDown} onClick={buildOnBackdropClick(cerrar)}>
          <ModalBox $maxWidth="580px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{editando ? 'Editar lugar de despacho' : 'Nuevo lugar de despacho'}</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
            </ModalHead>
            <ModalBody>
              <FormGrp>
                <Lbl>Nombre *</Lbl>
                <Inp autoFocus value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="ej. Planta Central, Obra Norte..." />
                {errors.nombre && <ErrMsg>{errors.nombre}</ErrMsg>}
              </FormGrp>

              {/* Mapa + dirección (igual que centros logísticos) */}
              <FormGrp>
                <Lbl>Ubicación en el mapa</Lbl>
                <LocationPicker
                  key={editando?.id ?? 'nuevo'}
                  value={mapLocation}
                  onChange={handleMapLocation}
                  placeholder="Buscar dirección del lugar de despacho..."
                />
                <Inp
                  style={{ marginTop: 6 }}
                  value={form.direccion}
                  onChange={e => set('direccion', e.target.value)}
                  placeholder="Dirección (se completa desde el mapa o escribir manualmente)"
                />
              </FormGrp>

              <Row2>
                <FormGrp style={{ marginBottom: 0 }}>
                  <Lbl>Teléfono</Lbl>
                  <Inp value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="ej. +54 9 11 1234-5678" />
                </FormGrp>
                <FormGrp style={{ marginBottom: 0 }}>
                  <Lbl>Contacto / Responsable</Lbl>
                  <Inp value={form.contacto} onChange={e => set('contacto', e.target.value)} placeholder="Nombre del responsable" />
                </FormGrp>
              </Row2>

              <FormGrp>
                <Lbl>Correo electrónico</Lbl>
                <Inp type="email" value={form.correo} onChange={e => set('correo', e.target.value)} placeholder="correo@ejemplo.com" />
                {errors.correo && <ErrMsg>{errors.correo}</ErrMsg>}
              </FormGrp>

              <FormGrp>
                <Lbl>Observaciones</Lbl>
                <Inp value={form.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Opcional" />
              </FormGrp>

              <FormGrp>
                <Lbl>
                  <input type="checkbox" checked={form.activo} onChange={e => set('activo', e.target.checked)} style={{ marginRight: 6 }} />
                  Activo
                </Lbl>
              </FormGrp>

              {/* Gestión de cisternas */}
              <SectionLabel>Cisternas del lugar</SectionLabel>
              {!editando ? (
                <EmptyHint>Guardá el lugar primero para poder agregar cisternas.</EmptyHint>
              ) : (
                <>
                  {cisternas.length === 0 && <EmptyHint>Este lugar no tiene cisternas registradas.</EmptyHint>}
                  {cisternas.map(c => (
                    <CisternaRow key={c.id}>
                      <CisternaNombre>
                        {c.nombre}
                        <small>
                          {c.identificacion ? `${c.identificacion} · ` : ''}
                          {c.capacidad_litros != null ? `${c.capacidad_litros} ${c.unidad_medida || 'L'}` : 'sin capacidad'}
                          {c.activo ? '' : ' · inactiva'}
                        </small>
                      </CisternaNombre>
                      <ActionBtn onClick={() => editarCisterna(c)}>Editar</ActionBtn>
                      <ActionBtn $danger onClick={() => eliminarCisterna(c.id)}>Eliminar</ActionBtn>
                    </CisternaRow>
                  ))}

                  <Row2 style={{ marginTop: '0.75rem' }}>
                    <FormGrp style={{ marginBottom: 0 }}>
                      <Lbl>Nombre de cisterna</Lbl>
                      <Inp value={cisternaForm.nombre} onChange={e => setCist('nombre', e.target.value)} placeholder="ej. Cisterna 1" />
                      {cisternaErr.nombre && <ErrMsg>{cisternaErr.nombre}</ErrMsg>}
                    </FormGrp>
                    <FormGrp style={{ marginBottom: 0 }}>
                      <Lbl>Identificación</Lbl>
                      <Inp value={cisternaForm.identificacion} onChange={e => setCist('identificacion', e.target.value)} placeholder="Patente / código" />
                    </FormGrp>
                  </Row2>
                  <Row2 style={{ marginTop: '0.5rem', alignItems: 'end' }}>
                    <FormGrp style={{ marginBottom: 0 }}>
                      <Lbl>Capacidad (litros)</Lbl>
                      <Inp type="number" min="0" step="0.01" value={cisternaForm.capacidad_litros} onChange={e => setCist('capacidad_litros', e.target.value)} placeholder="Opcional" />
                    </FormGrp>
                    <FormGrp style={{ marginBottom: 0 }}>
                      <BtnPrimary onClick={guardarCisterna} style={{ width: '100%' }}>
                        {cisternaEdit ? 'Actualizar cisterna' : 'Agregar cisterna'}
                      </BtnPrimary>
                    </FormGrp>
                  </Row2>
                  {cisternaEdit && (
                    <BtnSecondary style={{ marginTop: '0.5rem' }} onClick={() => { setCisternaEdit(null); setCisternaForm(EMPTY_CISTERNA); }}>
                      Cancelar edición de cisterna
                    </BtnSecondary>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={cerrar}>Cerrar</BtnSecondary>
              <BtnPrimary onClick={guardar} disabled={saving}>{saving ? 'Guardando...' : 'Guardar lugar'}</BtnPrimary>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {confirmId && (
        <Overlay onMouseDown={onBackdropMouseDown} onClick={buildOnBackdropClick(() => setConfirmId(null))}>
          <ModalBox $maxWidth="380px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>Confirmar eliminación</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setConfirmId(null)}>×</CloseBtn>
            </ModalHead>
            <ModalBody><p>¿Eliminar este lugar y sus cisternas? Esta acción no se puede deshacer.</p></ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setConfirmId(null)}>Cancelar</BtnSecondary>
              <BtnDanger onClick={() => eliminar(confirmId)}>Eliminar</BtnDanger>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {showImportModal && (
        <ImportarModal
          entidad="lugares-despacho"
          onClose={() => setShowImportModal(false)}
          onImportado={cargar}
        />
      )}

      {showImportCisternasModal && (
        <ImportarModal
          entidad="cisternas-despacho"
          onClose={() => setShowImportCisternasModal(false)}
          onImportado={cargar}
        />
      )}
    </Page>
  );
}
